import { QueryClient } from '@tanstack/react-query'

import { COP_API_BASE_URL, CUSTOMERS_ENDPOINT } from '../constants/service-endpoints'
import { get } from './http.service'

export type CustomerODataResponse<T> = {
    '@odata.context'?: string
    value: T[]
}

export interface FetchCustomersDataParams {
    /** Namespaces the react-query cache key so each caller keeps its own cache entries. */
    cacheKeyPrefix: string
    searchTerm?: string
    skip?: number
    /** Omit to fetch without a $top param (used by callers that need the full customer list). */
    top?: number
}

const customerSearchQueryClient = new QueryClient()

/**
 * Shared OData `customers` search, used by CopUserAccount, UserManagement and Subscription.
 * Matches on name/id startswith plus id contains, so partial/mid-string ids are found too.
 */
export async function fetchCustomersData<T>({
    cacheKeyPrefix,
    searchTerm,
    skip = 0,
    top,
}: FetchCustomersDataParams): Promise<CustomerODataResponse<T>> {
    return customerSearchQueryClient.fetchQuery<CustomerODataResponse<T>>({
        queryKey: [cacheKeyPrefix, searchTerm ?? '', skip, top ?? ''],
        staleTime: Infinity,
        queryFn: async () => {
            const queryParts: string[] = []
            if (top !== undefined) queryParts.push(`$top=${top}`)
            if (skip > 0) queryParts.push(`$skip=${skip}`)
            if (searchTerm) {
                const escaped = searchTerm.replace(/'/g, "''")
                queryParts.push(`$filter=startswith(name,'${escaped}') or startswith(id,'${escaped}') or contains(id,'${escaped}')`)
            }
            const qs = queryParts.length ? `?${queryParts.join('&')}` : ''
            return get<CustomerODataResponse<T>>(`${COP_API_BASE_URL}${CUSTOMERS_ENDPOINT}${qs}`)
        },
    })
}
